// src/pages/finance/BankAccounts.jsx
// Bank Accounts dashboard — summary cards + accounts list + create modal.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Landmark, Wallet, ArrowDownCircle, Loader2, X, Plus,
  AlertCircle, ChevronRight, CheckCircle2,
} from 'lucide-react';
import { bankAccountService } from '../../services/bankAccountService';

const ACCOUNT_TYPES = ['Current', 'Savings'];

// ── Summary Card (gradient + animated sweep) ─────────────────────────────
const SummaryCard = ({ icon: Icon, title, mainValue, subLabel, subValue, gradient, glow, onClick, mainValueClassName = '' }) => (
  <div
    onClick={onClick}
    className={
      'relative overflow-hidden rounded-card p-6 text-white shadow-elevated transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl ' +
      (onClick ? 'cursor-pointer ' : '') +
      gradient
    }
  >
    <div className="bank-card-sweep" />
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-4">
        <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-xs font-semibold uppercase tracking-widest text-white/70">{title}</p>
      <p className={'text-3xl font-bold mt-1 tracking-tight ' + mainValueClassName}>{mainValue}</p>
      <div className="mt-4 pt-3 border-t border-white/15 flex items-center justify-between">
        <span className="text-xs text-white/70">{subLabel}</span>
        <span className="text-sm font-semibold">{subValue}</span>
      </div>
    </div>
    <div className={'pointer-events-none absolute -bottom-8 -right-8 w-32 h-32 rounded-full blur-2xl opacity-40 ' + glow} />
  </div>
);

// ── Create Account Modal ─────────────────────────────────────────────────
const CreateAccountModal = ({ onClose, onDone }) => {
  const [form, setForm] = useState({
    bank_name: '', account_owner: '', account_number: '', iban: '',
    swift_code: '', branch_location: '', account_type: 'Current',
    opening_balance: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.bank_name.trim()) return setError('Bank name is required.');
    if (!form.account_owner.trim()) return setError('Account owner is required.');
    if (!form.account_number.trim()) return setError('Account number is required.');
    if (!form.iban.trim()) return setError('IBAN is required.');
    if (!form.branch_location.trim()) return setError('Branch location is required.');

    setSubmitting(true);
    try {
      await bankAccountService.createAccount({
        ...form,
        opening_balance: Number(form.opening_balance) || 0,
      });
      onDone();
    } catch (err) {
      setError(err.message || 'Failed to create bank account');
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary';
  const labelCls = 'block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between font-bold text-gray-900 sticky top-0 bg-white">
          Add Bank Account
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-control bg-red-50 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className={labelCls}>Bank Name</label>
            <input className={inputCls} value={form.bank_name} onChange={(e) => setField('bank_name', e.target.value)} placeholder="e.g. HBL, UBL, Meezan Bank" />
          </div>

          <div>
            <label className={labelCls}>Account Owner</label>
            <input className={inputCls} value={form.account_owner} onChange={(e) => setField('account_owner', e.target.value)} placeholder="Account holder full name" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Account Number</label>
              <input className={inputCls} value={form.account_number} onChange={(e) => setField('account_number', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>IBAN</label>
              <input className={inputCls} value={form.iban} onChange={(e) => setField('iban', e.target.value)} placeholder="PK00XXXX0000000000000000" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>SWIFT Code (optional)</label>
              <input className={inputCls} value={form.swift_code} onChange={(e) => setField('swift_code', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Account Type</label>
              <select className={inputCls} value={form.account_type} onChange={(e) => setField('account_type', e.target.value)}>
                {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Branch Location</label>
            <input className={inputCls} value={form.branch_location} onChange={(e) => setField('branch_location', e.target.value)} placeholder="Branch name / city" />
          </div>

          <div>
            <label className={labelCls}>Opening Balance</label>
            <input type="number" step="0.01" min="0" className={inputCls} value={form.opening_balance} onChange={(e) => setField('opening_balance', e.target.value)} placeholder="0.00" />
          </div>

          <div>
            <label className={labelCls}>Notes (optional)</label>
            <textarea className={inputCls + ' h-20'} value={form.notes} onChange={(e) => setField('notes', e.target.value)} />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2 shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Account
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────
const BankAccounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [cashAccount, setCashAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [accRes, sumRes, cashRes] = await Promise.all([
        bankAccountService.getAccounts(),
        bankAccountService.getSummary(),
        bankAccountService.getCashAccount(),
      ]);
      setAccounts(accRes.data || []);
      setSummary(sumRes.data || null);
      setCashAccount(cashRes.data || null);
    } catch (err) {
      setError('Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(''), 2000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const fmt = (n) => 'PKR ' + Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6 pb-10">
      <style>{`
        .bank-card-sweep {
          position: absolute;
          inset: 0;
          background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.28) 50%, transparent 70%);
          background-size: 250% 100%;
          background-position: 100% 0;
          animation: bankSweep 3.5s ease-in-out infinite;
        }
        @keyframes bankSweep {
          0% { background-position: 160% 0; }
          100% { background-position: -60% 0; }
        }
      `}</style>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-green-600 text-white px-5 py-3 rounded-control shadow-lg text-sm font-medium">
          <CheckCircle2 className="w-4 h-4" />
          {toast}
        </div>
      )}

      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Bank Accounts</h1>
          <p className="text-sm text-gray-500 font-medium tracking-tight">Company bank accounts and transaction history</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all"
        >
          <Plus className="w-4 h-4" />
          Add Bank Account
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SummaryCard
            icon={Landmark}
            title="Total Accounts"
            mainValue={summary.total_accounts}
            subLabel="Active Accounts"
            subValue={summary.active_accounts}
            gradient="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-900"
            glow="bg-purple-400"
          />
          <SummaryCard
            icon={Wallet}
            title="Total Balance"
            mainValue={fmt(summary.total_balance)}
            mainValueClassName={summary.total_balance < 0 ? 'text-red-700' : ''}
            subLabel="Total Deposit History"
            subValue={fmt(summary.total_deposits)}
            gradient="bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700"
            glow="bg-green-300"
          />
          <SummaryCard
            icon={ArrowDownCircle}
            title="Total Withdraw"
            mainValue={fmt(summary.total_withdrawals)}
            subLabel="This Month"
            subValue={fmt(summary.this_month_withdraw)}
            gradient="bg-gradient-to-br from-rose-500 via-red-600 to-red-800"
            glow="bg-red-300"
          />
         {cashAccount && (
            <SummaryCard
              icon={Wallet}
              title="Hand Cash"
              mainValue={fmt(cashAccount.current_balance)}
              mainValueClassName={cashAccount.current_balance < 0 ? 'text-red-300' : ''}
              subLabel="This Month Withdraw"
              subValue={fmt(cashAccount.this_month_withdraw)}
              gradient="bg-gradient-to-br from-amber-500 via-orange-600 to-amber-800"
              glow="bg-amber-300"
              onClick={() => navigate('/bank-accounts/CASH-00001')}
            />
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-control bg-red-50 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary opacity-20 w-10 h-10" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {accounts.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-card border border-dashed border-gray-200">
              <p className="text-gray-400 font-medium italic">No bank accounts added yet.</p>
            </div>
          ) : (
            accounts.map((acc) => (
              <div
                key={acc.account_id}
                onClick={() => navigate('/bank-accounts/' + acc.account_id)}
                className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center justify-between gap-4 hover:border-primary/20 cursor-pointer transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Landmark className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 group-hover:text-primary transition-colors">{acc.bank_name}</h3>
                    <p className="text-xs text-gray-500 font-medium">{acc.account_owner}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-gray-400 font-medium">Current Balance</p>
                    <p className={'text-sm font-bold ' + (acc.current_balance < 0 ? 'text-red-600' : 'text-gray-800')}>{fmt(acc.current_balance)}</p>
                  </div>
                  <span className={
                    'text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ' +
                    (acc.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500')
                  }>
                    {acc.status}
                  </span>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-primary transition-all" />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showModal && (
        <CreateAccountModal
          onClose={() => setShowModal(false)}
          onDone={() => { setShowModal(false); setToast('Bank account created successfully!'); loadData(); }}
        />
      )}
    </div>
  );
};

export default BankAccounts;